import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { ElementsWithSignature } from '@core/integrations-http-legacy';
import { hotspotToSignature } from '@core/integrations-http-legacy';
import type { Hotspot, IntegrationsElement } from '@core/integrations-types';
import { createEntityCollectionStore, memoizeSelector, pipeState } from '@core/ngrx-legacy';
import { wrapToArray } from '@core/utils-legacy';

const idSelector = ({ signature }: { signature: string }) => signature;
const embedElementAffected = (element: IntegrationsElement, partnerCodename: string): boolean =>
  !!(element.component === 'rti-partner-embed' && element.partner!.codename === partnerCodename);
const affectedByPartnerEntites = (
  items: Array<ElementsWithSignature | null>,
  partnerCodename: string,
): Array<ElementsWithSignature | null> =>
  items.filter(
    item => item && item.elements.some(element => embedElementAffected(element, partnerCodename)),
  );
const replaceTokenInSrc = (src: string, token: string): string => {
  const url = new URL(src);
  url.searchParams.set('token', token);
  return url.toString();
};
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<ElementsWithSignature>()('integration-elements', {
    idSelector,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => ({
      fetchCollection: (state, payload: Hotspot | Hotspot[]) =>
        adapter.markAsPending(state, {
          scope: null,
          entities: wrapToArray(payload).map(item => hotspotToSignature(item)),
        }),
      fetchCollectionSuccess: (state, payload: ElementsWithSignature[]) =>
        pipeState(
          state,
          next => adapter.writeMany(next, payload),
          next =>
            adapter.markAsLoaded(next, {
              scope: null,
              entities: payload.map(({ signature }) => signature),
            }),
        ),
      fetchCollectionFailed: (state, payload: { error: HttpErrorResponse; hotspots: Hotspot[] }) =>
        adapter.setError(state, {
          scope: null,
          entities: payload.hotspots.map(hotspot => hotspotToSignature(hotspot)),
        }),
      refreshPartnerEmbedToken: (
        state,
        payload: {
          partnerCodename: string;
        },
      ) => {
        const affected = affectedByPartnerEntites(
          Object.values(state.entityMap),
          payload.partnerCodename,
        );
        return adapter.markAsPending(state, {
          scope: null,
          entities: affected.map(item => item!.signature),
        });
      },
      refreshPartnerEmbedTokenSuccess: (
        state,
        payload: { token: string; partnerCodename: string },
      ) => {
        const affected = affectedByPartnerEntites(
          Object.values(state.entityMap),
          payload.partnerCodename,
        );
        const toUpdate = affected.map(item => ({
          ...item,
          elements: item!.elements.map(element => {
            if (embedElementAffected(element, payload.partnerCodename)) {
              return {
                ...element,
                inputs: {
                  ...element.inputs,
                  src: replaceTokenInSrc(element.inputs.src, payload.token),
                },
              };
            }
            return element;
          }),
        }));

        return pipeState(
          state,
          next => adapter.updateMany(next, toUpdate),
          next =>
            adapter.markAsLoaded(next, {
              scope: null,
              entities: affected.map(item => item!.signature),
            }),
        );
      },
      refreshPartnerEmbedTokenFailed: (
        state,
        payload: { error: HttpErrorResponse; partnerCodename: string },
      ) => {
        const affected = affectedByPartnerEntites(
          Object.values(state.entityMap),
          payload.partnerCodename,
        );
        return adapter.setError(state, {
          scope: null,
          entities: affected.map(item => item!.signature),
        });
      },
    }),
  });

// const IntegrationElementsActions: Pick<
//   typeof actions,
//   'fetchCollection' | 'fetchCollectionSuccess' | 'fetchCollectionFailed'
// > = actions;

const IntegrationElementsSelectors = {
  byHotspot: memoizeSelector((hotspot: Hotspot) =>
    createSelector(
      selectors.entityMap,
      entityMap => entityMap[hotspotToSignature(hotspot)]?.elements,
    ),
  ),
  metaByHotspot: memoizeSelector((hotspot: Hotspot) =>
    createSelector(
      selectors.entityMetaMap,
      entityMetaMap => entityMetaMap[hotspotToSignature(hotspot)],
    ),
  ),
};

export {
  actions as IntegrationElementsActions,
  namespace as IntegrationElementsNamespace,
  reducer as IntegrationElementsReducer,
  IntegrationElementsSelectors,
};
